import { useEffect, useMemo, useState } from "react";
import { gradeTranslation } from "../utils/SchoolAdmin/translations";
import { gradeNormalizer } from "../utils/GradeNormalizer";

/**
 * Derives subject options from the school level template and manages the
 * selected template.
 *
 * selectedTemplate ownership:
 * - When `externalSelectedTemplate` and `externalSetSelectedTemplate` are
 *   provided (GROUPS mode), the hook uses those and owns no template state.
 * - When they are omitted (HOMEROOM mode), the hook owns the state internally
 *   and returns it as `selectedTemplate` / `setSelectedTemplate`.
 *
 * GroupRatings lifts selectedTemplate so it can pass the same value to both
 * useGroupSelection (group options rebuild) and this hook.
 */
const useSubjectSelection = ({
    combinedGrades,
    selectedEpr,
    schoolLevelTemplateDto,
    incomingTemplateId,
    currentTimePeriodName,
    schoolLevelTemplateOverride,
    selectedGrade,
    selectedSchoolLocationKey,
    view,
    allSharedGroupOptions,
    gradingProfiles,
    sharedGradingProfileDtos,
    // Optional: when provided, the hook defers selectedTemplate ownership to the caller
    selectedTemplate: externalSelectedTemplate,
    setSelectedTemplate: externalSetSelectedTemplate
}) => {
    const [subjectOptions, setSubjectOptions] = useState([]);

    // Internal state used only when the caller does not provide external state
    const [internalSelectedTemplate, setInternalSelectedTemplate] = useState(null);

    const selectedTemplate =
        externalSelectedTemplate !== undefined ? externalSelectedTemplate : internalSelectedTemplate;
    const setSelectedTemplate =
        externalSetSelectedTemplate !== undefined ? externalSetSelectedTemplate : setInternalSelectedTemplate;

    const current = useMemo(() => {
        const templateSource = schoolLevelTemplateOverride || schoolLevelTemplateDto;
        const n = currentTimePeriodName ?? templateSource?.currentTimePeriodName ?? null;
        const first = templateSource?.firstSemester;
        const second = templateSource?.secondSemester;

        if (typeof n === "string") {
            const norm = n.toLowerCase();
            if (norm.startsWith("1st")) return first;
            if (norm.startsWith("2nd")) return second;
        }
        if (first?.gradeToSubjects?.length) return first;
        if (second?.gradeToSubjects?.length) return second;
        return undefined;
    }, [schoolLevelTemplateDto, schoolLevelTemplateOverride, currentTimePeriodName]);

    useEffect(() => {
        const gradeObjs = current?.gradeToSubjects || [];
        if (!gradeObjs.length) return;
        let subjects = [];
        if (view === "GROUPS" && combinedGrades?.length >= 1) {
            const gradeMatch = gradeObjs.find(
                (g) => gradeNormalizer(gradeTranslation[g.gradeLevel]) === gradeNormalizer(selectedGrade)
            );
            subjects = gradeMatch?.subjectToStandards || [];

            const hasHomeroom = gradingProfiles?.some((p) => p.gradeName === selectedGrade);

            // Filter subjects to only those the teacher has access to (EDIT or VIEW).
            // EDIT subjects render with editable cells; VIEW subjects render read-only.
            // Per-student editability is handled by RatingGridBody — not here.
            if (!hasHomeroom && allSharedGroupOptions?.length && sharedGradingProfileDtos?.length) {
                // Build map of criteria ID -> template ID from shared profiles
                const criteriaToTemplate = new Map();
                sharedGradingProfileDtos.forEach((profile) => {
                    profile.gradingCriteriaList?.forEach((criteria) => {
                        criteriaToTemplate.set(Number(criteria.key), Number(criteria.parentTemplate));
                    });
                });

                // Collect template IDs for all shared groups at the selected school,
                // regardless of permission level (EDIT or VIEW).
                // When no school is selected yet (null), include all schools so the
                // subject list isn't empty while the teacher is still picking a school.
                const matchingSharedGroups = allSharedGroupOptions.filter((g) => {
                    if (g.sharedGradeName && g.sharedGradeName !== selectedGrade) return false;
                    if (selectedSchoolLocationKey && Number(g.sharedLocationKey) !== Number(selectedSchoolLocationKey))
                        return false;
                    return true;
                });

                const accessibleTemplateIds = new Set();
                matchingSharedGroups.forEach((group) => {
                    group.sharedMeta?.groupShareDto?.gradingCriteriaShares?.forEach((share) => {
                        const templateId = criteriaToTemplate.get(Number(share.gradingCriteriaId));
                        if (templateId) accessibleTemplateIds.add(templateId);
                    });
                });

                // Matching shares at this grade/school means filter to subject shares only.
                // Empty accessibleTemplateIds is SLE-only (or no subject shares) → no academic subjects.
                // When nothing matches this grade yet, leave the full list (loading / wrong grade).
                if (matchingSharedGroups.length > 0) {
                    subjects = subjects.filter((subject) => accessibleTemplateIds.has(Number(subject.templateId)));
                }
            }
        } else if (selectedEpr?.grade) {
            const gradeMatch = gradeObjs.find((g) => Number(g.profileGradeId) === Number(selectedEpr.grade));
            subjects = gradeMatch?.subjectToStandards || [];
        } else if (Number.isFinite(incomingTemplateId)) {
            const holder = gradeObjs.find((g) =>
                (g.subjectToStandards || []).some((s) => Number(s.templateId) === Number(incomingTemplateId))
            );
            subjects = holder?.subjectToStandards || [];
        }

        setSubjectOptions((prev) => {
            if (prev === subjects) return prev;
            if (prev.length === subjects.length && prev.every((p, i) => p.templateId === subjects[i].templateId)) {
                return prev;
            }
            return subjects;
        });
    }, [
        combinedGrades,
        current,
        selectedEpr,
        selectedGrade,
        selectedSchoolLocationKey,
        incomingTemplateId,
        view,
        allSharedGroupOptions,
        gradingProfiles,
        sharedGradingProfileDtos
    ]);

    // Auto-select template from URL param (incomingTemplateId) once options are available.
    // When the caller owns the state (GROUPS mode), this calls the external setter.
    useEffect(() => {
        if (!Number.isFinite(incomingTemplateId)) return;
        if (!subjectOptions.length) return;
        const match = subjectOptions.find((s) => Number(s.templateId) === Number(incomingTemplateId));
        if (match) setSelectedTemplate(match);
    }, [incomingTemplateId, subjectOptions, setSelectedTemplate]);

    return { subjectOptions, selectedTemplate, setSelectedTemplate, setSubjectOptions };
};

export default useSubjectSelection;
